import { z } from 'zod'
import { calendarDate } from './dates'
import { itemSchema } from './items'

/**
 * The board's columns, left to right: time flows toward Now. The three Today columns (focus work,
 * the rest of today, now) are the board's focus. Moving a card to a column tells hugo's planner
 * when the user wants to do it (see `item.move`).
 */
export const boardColumns = [
  'backlog',
  'next_week',
  'this_week',
  'tomorrow',
  'focus',
  'today',
  'now',
  'waiting',
  'done',
] as const
export const boardColumnSchema = z.enum(boardColumns)
export type BoardColumn = z.output<typeof boardColumnSchema>

/** The columns that plan work (a card there is a task or a promise). */
export const boardWorkColumns: readonly BoardColumn[] = [
  'backlog',
  'next_week',
  'this_week',
  'tomorrow',
  'focus',
  'today',
  'now',
]

const minutes = z.number().int().min(0)

/** Time in days of the board, from hugo's planner (never the AI). */
export const boardTimeSchema = z.object({
  /** Minutes for tasks and promises in these days, after meetings and out of office. */
  capacityMinutes: minutes,
  /** Of those, the minutes hugo's plan fills (every card's work, wherever it sits). */
  plannedMinutes: minutes,
  /** Minutes of meetings in the working time. */
  meetingMinutes: minutes,
  /** Minutes out of office in the working time, beyond the meetings. */
  outOfOfficeMinutes: minutes,
})
export type BoardTime = z.output<typeof boardTimeSchema>

export const boardCardSchema = z.object({
  /** The item, with hugo's plan for it (`plan`: null when hugo does not plan it). */
  item: itemSchema,
  /** The user put it in this column (true), or hugo placed it there by its plan (false). */
  committed: z.boolean(),
  /**
   * A card the user put on a day or a week: whether all its work gets done by then (today: by
   * the end of today). Null for other cards.
   */
  fits: z.boolean().nullable(),
  /** A day card left over from an earlier day: that day. It counts as today's now. */
  carriedFrom: calendarDate.nullable(),
  /**
   * Personal (one of its tags is personal, decision 97): it takes none of the working hours, so
   * it has no plan and no fit check; hugo places it by its date.
   */
  personal: z.boolean().default(false),
})
export type BoardCard = z.output<typeof boardCardSchema>

export const boardColumnViewSchema = z.object({
  id: boardColumnSchema,
  /** The first and last day the column stands for; null for columns without days. */
  from: calendarDate.nullable(),
  to: calendarDate.nullable(),
  /**
   * Time in the column's days: Tomorrow, This week (its days after Tomorrow) and Next week.
   * Null for the others (the Today columns share `today.time`) and for weeks without days left.
   */
  time: boardTimeSchema.nullable(),
  /** The cards the user put here, in their order; then hugo's, by plan. */
  cards: z.array(boardCardSchema),
})
export type BoardColumnView = z.output<typeof boardColumnViewSchema>

/** `board.view`: the user's open work in columns, and their time, from hugo's planner. */
export const boardViewSchema = z.object({
  /** The user's local date. */
  date: calendarDate,
  /** Today has working hours and is neither a public holiday nor time off. */
  workday: z.boolean(),
  today: z.object({
    time: boardTimeSchema,
    /** Today's focus time from now: free blocks of an hour or more outside meetings. */
    focusBlocks: z.array(z.object({ startsAt: z.iso.datetime(), endsAt: z.iso.datetime() })),
    focusMinutes: minutes,
    /** The minutes hugo plans today for the focus work. */
    focusWorkMinutes: minutes,
  }),
  /** Every column, left to right. */
  columns: z.array(boardColumnViewSchema),
  /** Items waiting in the inbox (not on the board until sorted). */
  inbox: minutes,
  /** A calendar is connected and syncing: meetings count. */
  calendarConnected: z.boolean(),
})
export type BoardView = z.output<typeof boardViewSchema>

/**
 * What a move did to another card: `late` (it was on time for its due date and is not any more),
 * `no_fit` (it fit the day or week the user put it on and does not any more), `on_time` and
 * `fits` (the other way round), `moved` (hugo's own card went to another column).
 */
export const boardChangeKindSchema = z.enum(['late', 'no_fit', 'on_time', 'fits', 'moved'])
export type BoardChangeKind = z.output<typeof boardChangeKindSchema>

/** A card that changed because of a move. */
export const boardChangeSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  kind: boardChangeKindSchema,
  /** Its column before and after the move. */
  from: boardColumnSchema.nullable(),
  to: boardColumnSchema.nullable(),
  /** When its work gets done now; null when not within a year (or not planned). */
  projectedOn: calendarDate.nullable(),
  /** Its due day (for a promise: the day promised); null without one. */
  dueOn: calendarDate.nullable(),
})
export type BoardChange = z.output<typeof boardChangeSchema>

export const boardMoveResultSchema = z.object({
  item: itemSchema,
  /** The column the item is in now. */
  column: boardColumnSchema,
  /** Whether its work fits the day or week it went to (see boardCardSchema); null otherwise. */
  fits: z.boolean().nullable(),
  /**
   * Other cards the move changed: hugo's cards that went to another column, and items that are
   * late now or on time again. Late ones first.
   */
  changes: z.array(boardChangeSchema),
})
export type BoardMoveResult = z.output<typeof boardMoveResultSchema>
